import React, { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { CheckCircle2, Star, ArrowRight, MessageSquare, Pen } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const WEB3FORMS_KEY = '00ed92cb-5032-4578-b9be-306700b2271e';

const PROMPTS = [
  'Does the UI hold up under scrutiny?',
  'Is the AI builder worth the hype?',
  'What would you cut, fix, or redo?',
  'Would you recommend this to a client?',
];

function StarRating({ value, onChange }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex items-center gap-1.5">
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          onMouseEnter={() => setHovered(n)}
          onMouseLeave={() => setHovered(0)}
          className="transition-transform hover:scale-110 active:scale-95"
          aria-label={`${n} star${n > 1 ? 's' : ''}`}
        >
          <Star
            className={`w-8 h-8 transition-colors ${
              n <= (hovered || value)
                ? 'text-orange-400 fill-orange-400'
                : 'text-zinc-700 fill-zinc-700'
            }`}
          />
        </button>
      ))}
      {value > 0 && (
        <span className="ml-2 text-sm text-zinc-400">
          {['', 'Poor', 'Below average', 'Average', 'Good', 'Excellent'][value]}
        </span>
      )}
    </div>
  );
}

export default function Verdict() {
  const [form, setForm] = useState({
    name: '',
    role: '',
    platform: '',
    rating: 0,
    liked: '',
    improve: '',
    verdict: '',
    public: true,
  });
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.rating === 0) {
      setErrorMsg('Please select a star rating before submitting.');
      setStatus('error');
      return;
    }
    setStatus('loading');
    setErrorMsg('');

    const ratingLabel = ['', '★ Poor', '★★ Below average', '★★★ Average', '★★★★ Good', '★★★★★ Excellent'][form.rating];

    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          botcheck: false,
          subject: `Verdict — ${ratingLabel} from ${form.name || 'Anonymous'}${form.platform ? ` · ${form.platform}` : ''}`,
          from_name: form.name || 'Anonymous Critic',
          name: form.name || 'Anonymous',
          role: form.role,
          platform: form.platform,
          rating: `${form.rating}/5 — ${ratingLabel}`,
          what_worked: form.liked,
          what_needs_improvement: form.improve,
          overall_verdict: form.verdict,
          publish_publicly: form.public ? 'Yes' : 'No',
        }),
      });
      const data = await res.json();
      if (data.success) {
        setStatus('success');
      } else {
        throw new Error(data.message || 'Submission failed.');
      }
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message || 'Something went wrong. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-20 relative overflow-hidden">
      <SEOHead
        title="The Verdict — BuildersLab vs The Rest"
        description="See how BuildersLab compares to other website builders. Real reviews, feature comparisons, and honest feedback from our community."
        ogUrl="https://builderslab.net/Verdict"
      />

      {/* Ambient background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[700px] h-[700px] bg-[radial-gradient(ellipse_at_center,rgba(249,115,22,0.06)_0%,transparent_70%)]" />
      </div>

      <div className="max-w-2xl mx-auto px-6 relative z-10">

        {/* Header */}
        <div
         
         
         
          className="mb-14"
        >
          <div className="flex items-center gap-2 mb-6">
            <div className="w-8 h-px bg-orange-500" />
            <span className="text-orange-400 text-xs font-bold uppercase tracking-[0.2em]">Critic's Corner</span>
          </div>

          <h1 className="text-5xl md:text-6xl font-black text-white tracking-tight leading-none mb-5">
            Give Us<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-600">
              Your Verdict.
            </span>
          </h1>

          <p className="text-zinc-400 text-lg leading-relaxed max-w-lg">
            No PR spin. No softening. We want your unfiltered, professional take on BuildersLab —
            what works, what doesn't, and what we should kill.
          </p>

          {/* Prompt pills */}
          <div className="flex flex-wrap gap-2 mt-6">
            {PROMPTS.map(p => (
              <div
                key={p}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-900 border border-zinc-800 text-zinc-500 text-xs"
              >
                <MessageSquare className="w-3 h-3 shrink-0" />
                {p}
              </div>
            ))}
          </div>
        </div>

        {/* Form / Success */}
        <AnimatePresence mode="wait">
          {status === 'success' ? (
            <div
              key="success"
             
             
              className="text-center py-20 space-y-5"
            >
              <div className="w-16 h-16 rounded-2xl bg-emerald-500/15 border border-emerald-500/25 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8 text-emerald-400" />
              </div>
              <h2 className="text-2xl font-bold text-white">Verdict received.</h2>
              <p className="text-zinc-400 max-w-sm mx-auto leading-relaxed">
                Thank you for the honest take. Every critique makes BuildersLab sharper. We read every single one.
              </p>
            </div>
          ) : (
            <form
              key="form"
             
             
             
              onSubmit={handleSubmit}
              className="space-y-6"
            >

              {/* Identity */}
              <div className="grid sm:grid-cols-2 gap-4">
                <Field
                  label="Your name"
                  placeholder="Jane Doe"
                  value={form.name}
                  onChange={v => set('name', v)}
                  required={false}
                />
                <Field
                  label="Role / title"
                  placeholder="UX Designer, Tech Writer…"
                  value={form.role}
                  onChange={v => set('role', v)}
                  required={false}
                />
              </div>

              <Field
                label="Publication or platform"
                placeholder="Where do you publish? Blog URL, YouTube, podcast…"
                value={form.platform}
                onChange={v => set('platform', v)}
                required={false}
              />

              {/* Star rating */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-zinc-300">
                  Overall rating <span className="text-orange-500">*</span>
                </label>
                <StarRating value={form.rating} onChange={v => set('rating', v)} />
              </div>

              {/* What worked */}
              <TextareaField
                label="What works well"
                placeholder="Be specific — what genuinely impressed you?"
                value={form.liked}
                onChange={v => set('liked', v)}
                rows={3}
              />

              {/* What needs work */}
              <TextareaField
                label="What needs improvement"
                placeholder="Don't hold back. What's broken, missing, or just bad?"
                value={form.improve}
                onChange={v => set('improve', v)}
                rows={3}
              />

              {/* The verdict */}
              <TextareaField
                label={<>Your verdict <span className="text-orange-500">*</span></>}
                placeholder="Sum it up. Would you recommend BuildersLab? To whom, and why?"
                value={form.verdict}
                onChange={v => set('verdict', v)}
                rows={4}
                required
              />

              {/* Publish consent */}
              <label className="flex items-start gap-3 cursor-pointer group">
                <div className="relative mt-0.5 shrink-0">
                  <input
                    type="checkbox"
                    checked={form.public}
                    onChange={e => set('public', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-5 h-5 rounded-md bg-zinc-900 border border-zinc-700 peer-checked:bg-orange-500 peer-checked:border-orange-500 transition-colors flex items-center justify-center">
                    {form.public && (
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </div>
                <span className="text-sm text-zinc-400 leading-snug group-hover:text-zinc-300 transition-colors">
                  I'm happy for BuildersLab to share my verdict publicly (attributed to my name and platform).
                </span>
              </label>

              {/* Error */}
              <AnimatePresence>
                {status === 'error' && errorMsg && (
                  <p
                   
                   
                   
                    className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-2.5"
                  >
                    {errorMsg}
                  </p>
                )}
              </AnimatePresence>

              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full h-13 py-3.5 bg-white hover:bg-zinc-100 disabled:opacity-50 disabled:cursor-not-allowed text-zinc-950 font-bold text-base rounded-xl transition-all flex items-center justify-center gap-2.5 group shadow-[0_0_30px_rgba(255,255,255,0.08)] hover:shadow-[0_0_40px_rgba(255,255,255,0.15)]"
              >
                {status === 'loading' ? (
                  <>
                    <span className="w-4 h-4 border-2 border-zinc-400 border-t-zinc-900 rounded-full animate-spin" />
                    Submitting…
                  </>
                ) : (
                  <>
                    <Pen className="w-4 h-4" />
                    Submit Your Verdict
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                  </>
                )}
              </button>

              <p className="text-center text-zinc-600 text-xs">
                Anonymous submissions are welcome. We read every verdict personally.
              </p>
            </form>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function Field({ label, placeholder, value, onChange, required = false }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-zinc-300">
        {label}{required && <span className="text-orange-500 ml-0.5">*</span>}
      </label>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500/30 transition-all"
      />
    </div>
  );
}

function TextareaField({ label, placeholder, value, onChange, rows = 3, required = false }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-zinc-300">{label}</label>
      <textarea
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        required={required}
        className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500/30 transition-all resize-none"
      />
    </div>
  );
}
